import { useMutation, useMyUrl, useTranslation } from '@gimlite/common';
import {
  BadgeType,
  FormType,
  Segmented,
  SegmentedType,
  Widget,
  WidgetGroupType,
  Zone,
} from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  ArticleCodeStatus,
  ContractEntity,
  Mutation,
  MutationUpdatePoolContractArgs,
} from '../../client/graphql';
import { contractSessionsGql } from '../../common/gql/contract-sessions.gql';
import { updatePoolContractGql } from '../../common/gql/update-pool-contract.gql';
import { iconGlobalEntity } from '../../common/mapper/icon.mapper';
import {
  ContractBadgesType,
  useContractBadges,
} from '../../common/utils/contract-badges.hook';
import { ProfilDetails } from '../../components/profil-details/profil-details.component';
import { config } from '../../config';
import { PoolContractAction } from '../action/pool-contract.action';
import { ContractHistoryList } from '../list/history.list';
import { PoolAdminList } from '../list/pool-admin.list';
import { SessionList } from '../list/session.list';

type Status = 'ACTIVE' | 'SUSPENDED' | 'EXPIRED';

class Static {
  static statusBadgeColorMap: Record<Status, BadgeType.Config.Color> = {
    ACTIVE: 'disabled',
    SUSPENDED: 'warn',
    EXPIRED: 'disabled',
  };
}

export type TenantPoolContractDetailsProps = {
  contract: ContractEntity;
  onSelect?: (id: string) => void;
  widget?: {
    title?: string;
    state?: WidgetGroupType.Props['state'];
  };
};

export const TenantPoolContractDetails = observer(
  ({ onSelect, contract, widget }: TenantPoolContractDetailsProps) => {
    const { t, lang } = useTranslation();
    const [segmented, setSegmented] =
      useState<SegmentedType.Data.Selected>('admin');
    const [form, setForm] = useState<FormType.Data.Value>(
      extractFormData(contract),
    );
    const { setParamsUrl } = useMyUrl({});

    const [updatePoolContractCall, updatePoolContractState] = useMutation<
      Mutation['updatePoolContract'],
      MutationUpdatePoolContractArgs
    >(updatePoolContractGql);

    const handleSubmit = (form: FormType.Data.Value) => {
      const updatedContract = {
        name1: form.name1 || '',
        name2: form.name2 || '',
        startDate: form.startDate,
        stopDate: form.endDate,
        spacesAllocated: Number(form.noOfSpace[1]),
        description: form.description || '',
        spacesOccupied: Number(form.noOfSpace[0]),
        reference: form.reference,
        articleCode: form.articleCode,
      } as Partial<ContractEntity>;

      updateContract(updatedContract);
    };

    const updateContract = async (updatedContract: Partial<ContractEntity>) => {
      setParamsUrl({ clicked: undefined });

      try {
        await updatePoolContractCall({
          variables: {
            contractId: contract._id,
            input: updatedContract,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

    const isStopped = contract.stopDate
      ? DateTime.fromISO(contract.stopDate).diffNow().as('milliseconds') < 0
      : false;
    const isUpcoming = contract.startDate
      ? DateTime.fromISO(contract.startDate) > DateTime.now()
      : false;
    const states: [ContractBadgesType.Data.State] = [
      contract.isEnabled ? 'ACTIVE' : 'SUSPENDED',
    ];
    if (isUpcoming) states.push('UPCOMING');
    if (isStopped) states.push('EXPIRED');
    if (isStopped || isUpcoming) states.shift();
    const badges = useContractBadges({ states });

    useEffect(() => {
      const {
        ospContractId,
        startDate,
        stopDate,
        name1,
        name2,
        spacesAllocated,
        description,
        reference,
        spacesOccupied,
        articleCode,
      } = contract;
      setForm({
        name1,
        name2,
        code: ospContractId,
        reference: reference,
        noOfSpace: [
          `${config.PORTAL === 'tenant' ? '/' : ''}${spacesOccupied}`,
          `${spacesAllocated}`,
        ],
        startDate,
        endDate: stopDate,
        description,
        articleCode,
      });
    }, [JSON.stringify(contract)]);

    return (
      <Widget.Group
        config={{
          title: widget?.title,
          backtitle: !!widget?.title,
        }}
        state={widget?.state}
      >
        <Zone
          config={{
            zones: [['profil'], ['segmented'], ['details']],
            rows: ['min-content', 'min-content', '1fr'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area config={{ area: 'profil' }}>
            <Widget>
              <ProfilDetails
                handleEvent={{ submit: handleSubmit }}
                data={{
                  icon: iconGlobalEntity['masterPoolContract'],
                  form: form,
                  badges: badges,
                }}
                config={{
                  form: {
                    title1: {
                      name: 'name2',
                      placeholder: t('name2'),
                    },
                    title2: {
                      name: 'name1',
                      placeholder: t('name1'),
                    },
                    description1: {
                      name: 'code',
                      placeholder: t('code'),
                      edit: false,
                    },
                    description2: {
                      name: 'reference',
                      placeholder: t('reference'),
                    },
                    group1: [
                      {
                        name: 'noOfSpace',
                        label: t('noOfSpace'),
                        element: { type: 'counter' },
                        render: (value) =>
                          `${value[0].replace('/', '')} / ${value[1].replace(
                            '/',
                            '',
                          )}`,
                      },
                      {
                        name: 'articleCode',
                        label: t('codePMS'),
                        element: {
                          type: 'select',
                          items: contract
                            .product!.externalPayload?.ArticleCodes?.filter(
                              (code) =>
                                code.status === ArticleCodeStatus.Active,
                            )
                            .map(({ code }) => ({
                              label: code,
                              value: code,
                            })),
                        },
                      },
                      {
                        name: 'startDate',
                        label: t('startDate'),
                        element: {
                          type: 'datetime',
                          min: contract.dateBoundaryFromParent?.from || null,
                          max:
                            contract.stopDate ||
                            contract.dateBoundaryFromParent?.to,
                        },
                        render: (value: string) =>
                          DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                      },
                      {
                        name: 'endDate',
                        label: t('endDate'),
                        element: {
                          type: 'datetime',
                          min:
                            contract.startDate ||
                            contract.dateBoundaryFromParent?.from,
                          max: contract.dateBoundaryFromParent?.to || null,
                        },
                        render: (value: string) =>
                          DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                      },
                      {
                        name: 'description',
                        label: t('description'),
                        element: { type: 'textarea' },
                      },
                    ],
                  },
                  actions: (
                    <PoolContractAction
                      config={{ size: 'xlarge' }}
                      initialState={{
                        id: contract._id,
                        isStopped: isStopped,
                        status: contract.isEnabled,
                      }}
                    />
                  ),
                }}
              />
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'segmented' }}>
            <Zone.Area config={{ area: 'segmented' }}>
              <Widget>
                <Segmented
                  handleEvent={{ option: setSegmented }}
                  data={{ selected: segmented }}
                  config={{
                    size: 'large',
                    options: [
                      { label: t('admins'), value: 'admin' },
                      { label: t('sessions'), value: 'session' },
                      { label: t('history'), value: 'history' },
                    ],
                  }}
                />
              </Widget>
            </Zone.Area>
          </Zone.Area>

          <Zone.Area config={{ area: 'details' }}>
            {segmented === 'admin' && contract && (
              <PoolAdminList contract={contract} />
            )}
            {segmented === 'session' && contract && (
              <SessionList
                overrideGQL={contractSessionsGql}
                parkingId={contract.parkingId}
                contractId={contract._id}
                category={contract.category}
              />
            )}
            {segmented === 'history' && contract && (
              <ContractHistoryList contractId={contract._id} />
            )}
          </Zone.Area>
        </Zone>
      </Widget.Group>
    );
  },
);

function extractFormData(contract: ContractEntity): FormType.Data.Value {
  const {
    ospContractId,
    startDate,
    stopDate,
    name1,
    name2,
    spacesAllocated,
    description,
    reference,
    spacesOccupied,
  } = contract;

  return {
    name1,
    name2,
    code: ospContractId,
    reference: reference,
    noOfSpace: [`${spacesOccupied}`, `${spacesAllocated}`],
    startDate,
    endDate: stopDate,
    description,
  };
}
